import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, Button, Space, Spin, Alert, Tag, message, Typography } from 'antd';
import { PlusOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { listPipelines, listStages, type Pipeline, type PipelineStage } from '@/api/pipelineApi';
import {
  listOpportunities, updateOpportunity,
  type Opportunity, type UpdateOpportunityRequest,
} from '@/api/opportunityApi';
import { useAuth } from '@/hooks/useAuth';

const { Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
  open: '#1890ff',
  won: '#52c41a',
  lost: '#ff4d4f',
  suspended: '#faad14',
};

const STAGE_HEADER_COLORS: Record<string, string> = {
  won: '#f6ffed',
  lost: '#fff2f0',
  default: '#fafafa',
};

function getStageHeaderBg(stage: PipelineStage): string {
  if (stage.is_won) return STAGE_HEADER_COLORS.won;
  if (stage.is_lost) return STAGE_HEADER_COLORS.lost;
  return STAGE_HEADER_COLORS.default;
}

export default function OpportunityKanbanPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canWrite = hasPermission('opportunities', 'write');

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>('');
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);

  // Ref for the dragged opportunity
  const draggedOpp = useRef<Opportunity | null>(null);

  // Load pipelines on mount
  useEffect(() => {
    listPipelines({ from: 0, size: 100 })
      .then((res) => {
        setPipelines(res.items);
        // Auto-select first (or default) pipeline
        const defaultPipe = res.items.find((p) => p.is_default) ?? res.items[0];
        if (defaultPipe) {
          setSelectedPipelineId(defaultPipe.id);
        }
      })
      .catch(() => setPipelines([]));
  }, []);

  // Load stages + opportunities when pipeline changes
  const loadBoard = useCallback(async (pipelineId: string) => {
    if (!pipelineId) return;
    setLoading(true);
    setError(null);
    try {
      const [stageRes, oppRes] = await Promise.all([
        listStages(pipelineId),
        listOpportunities({ from: 0, size: 500 }, { pipeline_id: pipelineId }),
      ]);
      setStages(stageRes.items);
      setOpportunities(oppRes.items);
      setTruncated(oppRes.total > 500);
    } catch {
      setError(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (selectedPipelineId) {
      loadBoard(selectedPipelineId);
    }
  }, [selectedPipelineId, loadBoard]);

  // --- Drag & Drop handlers ---

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, opp: Opportunity) => {
      draggedOpp.current = opp;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', opp.id);
      // Make card semi-transparent while dragging
      const target = e.currentTarget;
      requestAnimationFrame(() => {
        target.style.opacity = '0.4';
      });
    },
    []
  );

  const handleDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.opacity = '1';
    setDragOverStageId(null);
    draggedOpp.current = null;
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, stageId: string) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverStageId(stageId);
    },
    []
  );

  const handleDragLeave = useCallback(() => {
    setDragOverStageId(null);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>, targetStageId: string) => {
      e.preventDefault();
      setDragOverStageId(null);

      const opp = draggedOpp.current;
      if (!opp || opp.stage_id === targetStageId || !canWrite) return;

      setMovingId(opp.id);

      // Optimistic UI update
      setOpportunities((prev) =>
        prev.map((o) =>
          o.id === opp.id ? { ...o, stage_id: targetStageId } : o
        )
      );

      try {
        const targetStage = stages.find((s) => s.id === targetStageId);
        const updateData: UpdateOpportunityRequest = {
          name: opp.name,
          customer_id: opp.customer_id,
          pipeline_id: opp.pipeline_id,
          stage_id: targetStageId,
          amount: opp.amount,
          probability: targetStage?.probability ?? opp.probability,
          close_date: opp.close_date,
        };
        await updateOpportunity(opp.id, updateData);
        // Update probability in local state after successful API call
        setOpportunities((prev) =>
          prev.map((o) =>
            o.id === opp.id
              ? { ...o, stage_id: targetStageId, probability: targetStage?.probability ?? o.probability }
              : o
          )
        );
      } catch {
        // Revert optimistic update
        setOpportunities((prev) =>
          prev.map((o) =>
            o.id === opp.id ? { ...o, stage_id: opp.stage_id } : o
          )
        );
        message.error(t('app.error'));
      } finally {
        setMovingId(null);
      }
    },
    [canWrite, stages, t]
  );

  // Sort stages by sort_order for correct column ordering
  const sortedStages = useMemo(
    () => [...stages].sort((a, b) => a.sort_order - b.sort_order),
    [stages]
  );

  // Group opportunities by stage
  const oppByStage = sortedStages.reduce<Record<string, Opportunity[]>>((acc, stage) => {
    acc[stage.id] = opportunities.filter((o) => o.stage_id === stage.id);
    return acc;
  }, {});

  const pipelineOptions = pipelines.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <h2 style={{ margin: 0 }}>{t('opportunities.kanban_title')}</h2>
          <Select
            value={selectedPipelineId || undefined}
            placeholder={t('opportunities.select_pipeline')}
            style={{ width: 200 }}
            options={pipelineOptions}
            onChange={setSelectedPipelineId}
          />
        </Space>
        <Space>
          <Button
            icon={<UnorderedListOutlined />}
            onClick={() => navigate('/opportunities')}
          >
            {t('opportunities.list_view')}
          </Button>
          {canWrite && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() =>
                navigate(
                  `/opportunities/new${selectedPipelineId ? `?pipeline_id=${selectedPipelineId}` : ''}`
                )
              }
            >
              {t('opportunities.new_opportunity')}
            </Button>
          )}
        </Space>
      </Space>

      {error && (
        <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />
      )}

      {truncated && (
        <Alert type="warning" message={t('opportunities.kanban_truncated')} style={{ marginBottom: 16 }} />
      )}

      {loading ? (
        <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
      ) : sortedStages.length === 0 ? (
        <Alert type="info" message={t('opportunities.no_stages')} />
      ) : (
        <div
          style={{
            display: 'flex',
            gap: 12,
            overflowX: 'auto',
            paddingBottom: 16,
            minHeight: 400,
          }}
        >
          {sortedStages.map((stage) => {
            const stageOpps = oppByStage[stage.id] ?? [];
            const isOver = dragOverStageId === stage.id;

            return (
              <div
                key={stage.id}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stage.id)}
                style={{
                  minWidth: 260,
                  maxWidth: 300,
                  flex: '0 0 260px',
                  background: isOver ? '#e6f7ff' : '#f5f5f5',
                  borderRadius: 8,
                  border: isOver ? '2px dashed #1890ff' : '2px solid transparent',
                  transition: 'background 0.2s, border 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Stage Header */}
                <div
                  style={{
                    padding: '12px 12px 8px',
                    background: getStageHeaderBg(stage),
                    borderRadius: '8px 8px 0 0',
                    borderBottom: '1px solid #e8e8e8',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text strong style={{ fontSize: 14 }}>{stage.name}</Text>
                    <Tag>{stageOpps.length}</Tag>
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {stage.probability}%
                    {Object.entries(
                      stageOpps.reduce<Record<string, number>>((acc, o) => {
                        const cur = o.currency || 'TWD';
                        acc[cur] = (acc[cur] || 0) + (o.amount || 0);
                        return acc;
                      }, {})
                    ).map(([cur, sum]) => (
                      <span key={cur}> &middot; {cur} {sum.toLocaleString()}</span>
                    ))}
                  </Text>
                </div>

                {/* Cards */}
                <div
                  style={{
                    flex: 1,
                    padding: 8,
                    overflowY: 'auto',
                    minHeight: 100,
                  }}
                >
                  {stageOpps.map((opp) => (
                    <div
                      key={opp.id}
                      draggable={canWrite}
                      onDragStart={(e) => handleDragStart(e, opp)}
                      onDragEnd={handleDragEnd}
                      onClick={() => navigate(`/opportunities/${opp.id}`)}
                      style={{
                        background: '#fff',
                        borderRadius: 6,
                        padding: '10px 12px',
                        marginBottom: 8,
                        cursor: canWrite ? 'grab' : 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        opacity: movingId === opp.id ? 0.5 : 1,
                        transition: 'opacity 0.2s, box-shadow 0.2s',
                        borderLeft: `3px solid ${STATUS_COLORS[opp.status] ?? '#d9d9d9'}`,
                      }}
                    >
                      <div style={{ fontWeight: 500, marginBottom: 4, fontSize: 13 }}>
                        {opp.name}
                      </div>
                      {opp.amount > 0 && (
                        <div style={{ fontSize: 12, color: '#1890ff', marginBottom: 2 }}>
                          {opp.currency || 'TWD'} {opp.amount.toLocaleString()}
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {opp.close_date
                            ? new Date(opp.close_date).toLocaleDateString()
                            : ''}
                        </Text>
                        <Tag
                          color={STATUS_COLORS[opp.status] ?? 'default'}
                          style={{ fontSize: 11, marginRight: 0, lineHeight: '18px' }}
                        >
                          {t(`opportunities.status_options.${opp.status}`, opp.status)}
                        </Tag>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
